import { differenceWith, intersectionWith, isEqual } from "lodash";

import type BusbudTerms from "@busbud/busbud-terms";
import type { TicketType } from "@busbud/int-schemas";
import type { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import type { MappedOperator } from "@busbud/int-schemas/udm";

import type { SimplifiedTerms } from "@app/modules/terms/flatten-terms";
import getSimplifiedTerms from "@app/modules/terms/flatten-terms";
import type { TripOption } from "@app/types/results-types";

// Maintains reference between renders
const EMPTY_ARR: any[] = [];

interface GlobalTerms {
  title?: string;
  items: SimplifiedTerms[];
}

type FareTerms = Omit<TripOption, "terms"> & {
  terms: {
    title: string | undefined;
    items: SimplifiedTerms[];
  }[];
};

type FlattenedFareTerms = Omit<TripOption, "terms"> & {
  terms: SimplifiedTerms[];
};

export const extractTerms = (
  termsBuilder: BusbudTerms,
  ticket_type: TicketType | null | undefined,
  operator: MappedOperator | undefined,
  trip_type: string,
  vehicle_type: VehicleType,
  fares?: TripOption[]
): {
  global_terms: GlobalTerms[];
  fares_terms: FlattenedFareTerms[];
} => {
  if (!fares?.length) {
    return { global_terms: EMPTY_ARR, fares_terms: EMPTY_ARR };
  }
  const fares_terms: FareTerms[] = fares.map(({ terms, ...fare }) => {
    const all_terms = termsBuilder.buildAllTerms({
      ticket_type: ticket_type ?? null,
      terms,
      operators: operator ? [operator] : EMPTY_ARR,
      trip_type,
      vehicle_type,
      options: { use_simplified_terms: true }
    });

    return {
      ...fare,
      terms: all_terms.map(({ title, ...terms }) => ({
        title,
        items: getSimplifiedTerms(terms)
      }))
    };
  });

  // If there is only one fare we display all together in the global terms
  if (fares_terms.length === 1) {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const global_terms = fares_terms[0].terms.filter(
      terms => !!terms.items.length
    );

    return { fares_terms: EMPTY_ARR, global_terms };
  }

  // Extracts the terms shared by all fare in a global object
  const global_terms: GlobalTerms[] = fares_terms
    .reduce((global_terms, { terms }, index) => {
      if (!index) {
        return terms;
      }
      return intersectionWith(global_terms, terms, isEqual);
    }, [] as GlobalTerms[])
    .filter(terms => !!terms.items.length);

  // Filters the global terms from each fare
  const filtered_fares_terms = fares_terms.map(fare => ({
    ...fare,
    terms: differenceWith(fare.terms, global_terms, isEqual).flatMap(
      terms => terms.items
    )
  }));

  return { global_terms, fares_terms: filtered_fares_terms };
};
